import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-message-stream>` says about itself.
 *
 * Four things are worth knowing before placing it, and none of them is visible in the input list.
 *
 * It draws the MESSAGES and nothing around them. No channel title, no composer, no menu — a
 * conversation surface owns those and puts the stream between them.
 *
 * A new message scrolls the view only for a reader who was already at the bottom. Somebody reading
 * back through yesterday keeps their place, and a control appears offering to take them to the
 * latest, so nothing is silently missed either way.
 *
 * The unread line is fixed when the conversation opens and does not move while the reader is in it.
 * That is why `key` exists: it is how this element is told a DIFFERENT conversation is now on
 * screen and the line may be placed again.
 *
 * Grouping is applied here, not by the bubbles. Consecutive messages from one person within a few
 * minutes and on the same day are drawn as one block — and anything that breaks the run, a day
 * divider or the unread line, breaks the grouping too, because a divider between two messages and
 * those two drawn as one block are contradictory statements about the same pair.
 */
const docs: ComponentDocs = {
  aliases: ['chat log', 'message list', 'conversation', 'thread view', 'chat history', 'transcript'],

  answers: [
    'show a conversation people can scroll back through',
    'draw messages with day separators',
    'mark where somebody stopped reading',
    'keep a conversation readable while new messages arrive',
  ],

  placements: ['recordPage', 'homePage', 'appPage'],

  inputs: [
    {
      name: 'key',
      attribute: 'key',
      type: 'text',
      description:
        'Which conversation this is. Changing it is what tells the stream a different conversation is ' +
        'being read, and is the only thing that lets the unread line be placed again. A stream with no ' +
        'key behaves as one conversation that never changes.',
    },
    {
      name: 'unreadSince',
      attribute: 'unread-since',
      type: 'text',
      description:
        'When the reader last saw this conversation, as an ISO-8601 instant. The first message after ' +
        'it gets a "New messages" line above it. Read ONCE per conversation and then left alone — a ' +
        'line recomputed as messages arrive walks down the page ahead of the reader and marks nothing.',
    },
    {
      name: 'highlight',
      attribute: 'highlight',
      type: 'text',
      description:
        'The id of the message that was navigated to — from a search result or a link. That message is ' +
        'marked; setting it does not scroll. Use the `reveal` method to do both.',
    },
    {
      name: 'emptyLabel',
      attribute: 'empty-label',
      type: 'text',
      default: 'No messages yet.',
      description:
        'What a conversation with nothing in it says. Worth setting: the default says a true thing ' +
        'about a list and nothing about what the reader could do next.',
    },
  ],

  events: [],

  state: [],

  slots: [
    {
      name: 'pending',
      takes:
        'What sits below the last message and above the composer — a `<caos-typing-indicator>`, or ' +
        'anything else that says the next message is on its way. Inside the scroller, so it is ' +
        'reached by scrolling to the bottom like a message is.',
    },
  ],

  tokens: [
    { token: '--caos-color-text', controls: 'The message text, and the jump control.' },
    { token: '--caos-color-text-subtle', controls: 'A day divider’s words.' },
    { token: '--caos-color-text-muted', controls: 'The empty state.' },
    { token: '--caos-color-border', controls: 'The rules either side of a divider, and the jump control’s edge.' },
    { token: '--caos-color-accent', controls: 'The unread line — its words and its rules, so it reads as different from a day.' },
    { token: '--caos-color-surface', controls: 'Behind the jump control, so it stays legible over messages.' },
    { token: '--caos-font', controls: 'The typeface of the dividers and the jump control.' },
    { token: '--caos-text-label', controls: 'The size of a divider’s words.' },
    { token: '--caos-text-body', controls: 'The size of the empty state.' },
    { token: '--caos-font-weight-label', controls: 'The weight of a divider’s words.' },
    { token: '--caos-space-2', controls: 'The space above and below the list, and around a divider.' },
    { token: '--caos-space-3', controls: 'The inset of a divider, and where the jump control sits.' },
    { token: '--caos-space-4', controls: 'The padding of the empty state.' },
    { token: '--caos-radius-pill', controls: 'The roundness of the jump control.' },
    { token: '--caos-shadow-float', controls: 'The lift under the jump control, which floats over the messages.' },
    { token: '--caos-focus-ring', controls: 'The ring on the jump control.' },
  ],

  parts: [
    { part: 'scroller', is: 'the scrolling region' },
    { part: 'divider', is: 'a day divider' },
    { part: 'unread', is: 'the new-messages line' },
    { part: 'jump', is: 'the control that takes the reader to the latest message' },
    { part: 'empty', is: 'the line shown when there are no messages' },
  ],

  methods: [
    {
      name: 'scrollToLatest',
      description: 'Take the reader to the newest message, as the jump control does.',
    },
    {
      name: 'isFollowing',
      description:
        'Is the reader at the living end of the conversation? The same question the stream asks itself ' +
        'before deciding whether an arriving message should scroll the view.',
    },
    {
      name: 'reveal',
      description:
        'Bring one message into view and mark it. False when this stream is not holding that message — ' +
        'which is the answer a search result needs, because the message may be in a conversation that ' +
        'is not open.',
    },
  ],

  placing:
    'As the tall part of a conversation: a title above it, a composer below it, and this filling ' +
    'what is left. It is a flex column with its own scroller, so it needs a parent with a HEIGHT — ' +
    'in a container that merely grows, nothing scrolls and the page does instead.',

  whenToUse:
    'Messages that arrive over time and are read in order, where the reader scrolls back. The day ' +
    'dividers, the unread line and the grouping are all statements about a sequence; content with no ' +
    'sequence gets none of them and wants a list.',

  insteadUse: [
    {
      name: 'DataTable',
      when:
        'The rows are records to be compared, sorted or selected. A conversation is read downwards and ' +
        'never sorted.',
    },
    {
      name: 'Accordion',
      when: 'The entries are sections of a document that open and close, rather than things said in order.',
    },
    {
      name: 'Tree',
      when: 'The shape that matters is nesting rather than time.',
    },
  ],

  accessibility:
    'The stream is a `role="log"` with `aria-live="polite"`: a region content is APPENDED to, announced ' +
    'as additions rather than as a region that was replaced, and politely — a message arriving must not ' +
    'interrupt a sentence the reader is in the middle of, since what it would interrupt them with is ' +
    'more of the same conversation. Day dividers and the unread line are `role="separator"` with a ' +
    'name, so the break is announced as a break instead of being read as part of the message under it. ' +
    'Each message is its own article, so a reader can move message by message. Left to the author: ' +
    'naming the stream — set `aria-label` to say WHICH conversation this is, because "log" on its own ' +
    'says only that it is one.',

  examples: [
    {
      key: 'conversation',
      label: 'A conversation in progress',
      summary:
        'Two people, with the second message grouped under the first because it is the same person a ' +
        'minute later.',
      placement: {
        component: 'caos-message-stream',
        inputs: { 'aria-label': 'Deploys' },
      },
    },
  ],
};

export default docs;
